import { useContext, useCallback } from 'react';

import { logError } from '~/common/utils/log';
import { UserContext } from '~/components/providers/UserProvider';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { SocureKycStatus } from '~/services/users/types';
import { getHasPaidContestsV2 } from '~/services/contests/contests-v2.service.api';
import isWebView from '~/utils/isWebView';

import { useIntercomJwt } from './useIntercomJwt';

function useOpenIntercom() {
  const { user, userDetails } = useContext(UserContext);
  const { data: walletBalance } = useWalletBalance();
  const webView = isWebView();
  const { jwt, isLoading: isJwtLoading } = useIntercomJwt();

  // Keep in sync with IntercomLoggedIn's render gate: it assigns window.Intercom during render, which this hook cannot observe.
  const isIntercomAvailable = !webView && !!user?.email && !isJwtLoading;

  const openIntercom = useCallback(
    async (message) => {
      if (!isIntercomAvailable) {
        return;
      }

      const isPaidUser =
        userDetails.verificationStatus === SocureKycStatus.ACCEPT &&
        (walletBalance?.availableBalance > 0 ||
          userDetails.vippRegistrationId ||
          userDetails.paypalPayerId ||
          userDetails.paysafeId ||
          userDetails.venmoId ||
          userDetails.aeropayId);

      let isPaidCommissioner = false;
      try {
        const hasPaidContestsResponse = await getHasPaidContestsV2();

        if (hasPaidContestsResponse?.data?.hasPaidContests) {
          isPaidCommissioner = true;
        }
      } catch (error) {
        logError('Error getting commissioner contests for Intercom update', error);
      }

      // Update the intercom user traits
      window.Intercom('update', {
        paid_user: isPaidUser,
        paid_commissioner: isPaidCommissioner,
        email: user.email,
        handle: user.handle,
        name: `${user.firstName} ${user.lastName}`,
        user_id: user.id,
        ...(jwt && { intercom_user_jwt: jwt }),
      });

      // Open the intercom message window
      window.Intercom('showNewMessage', message);
    },
    [isIntercomAvailable, user, userDetails, walletBalance, jwt]
  );

  return { openIntercom, isIntercomAvailable };
}

export default useOpenIntercom;
